import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { INTRO_SESSION_KEY } from '../constants/authIntro';
import { useAuthStore } from '../store/authStore';
import { useUiStore } from '../store/uiStore';

/**
 * Convenience hook wrapping the auth store with navigation + toast side effects.
 * Keeps components free of direct router / toast imports.
 */
export function useAuth() {
  const navigate = useNavigate();
  const user = useAuthStore((s) => s.user);
  const token = useAuthStore((s) => s.token);
  const loading = useAuthStore((s) => s.loading);
  const error = useAuthStore((s) => s.error);
  const storeLogin = useAuthStore((s) => s.login);
  const storeRegister = useAuthStore((s) => s.register);
  const storeLogout = useAuthStore((s) => s.logout);
  const storeUpdateProfile = useAuthStore((s) => s.updateProfile);
  const pushToast = useUiStore((s) => s.pushToast);

  // Decide where to go after a successful login/register. New users (and
  // existing users who haven't added any profile URLs) get routed to the
  // onboarding step first.
  const postAuthDestination = (user) =>
    user?.has_social_links ? '/dashboard' : '/onboarding/social-links';

  const login = useCallback(
    async (credentials) => {
      const result = await storeLogin(credentials);
      if (result.success) {
        pushToast({ type: 'success', title: 'Welcome back!', message: 'Signed in successfully.' });
        navigate(postAuthDestination(result.user), { replace: true });
      } else {
        pushToast({ type: 'error', title: 'Login failed', message: result.error });
      }
      return result;
    },
    [navigate, pushToast, storeLogin]
  );

  const register = useCallback(
    async (payload) => {
      const result = await storeRegister(payload);
      if (result.success) {
        pushToast({ type: 'success', title: 'Account created', message: 'Welcome aboard!' });
        // New users always start at onboarding.
        navigate('/onboarding/social-links', { replace: true });
      } else {
        pushToast({ type: 'error', title: 'Registration failed', message: result.error });
      }
      return result;
    },
    [navigate, pushToast, storeRegister]
  );

  const logout = useCallback(async () => {
    await storeLogout();
    if (typeof window !== 'undefined') {
      window.sessionStorage.removeItem(INTRO_SESSION_KEY);
    }
    pushToast({ type: 'info', title: 'Signed out', message: 'See you soon.' });
    navigate('/login', { replace: true });
  }, [navigate, pushToast, storeLogout]);

  const updateProfile = useCallback(
    async (patch) => {
      const result = await storeUpdateProfile(patch);
      if (result.success) {
        pushToast({
          type: 'success',
          title: 'Profile saved',
          message: 'Your changes have been updated.',
        });
      } else {
        pushToast({
          type: 'error',
          title: 'Update failed',
          message: result.error,
        });
      }
      return result;
    },
    [pushToast, storeUpdateProfile]
  );

  return {
    user,
    token,
    loading,
    error,
    isAuthenticated: Boolean(token),
    login,
    register,
    logout,
    updateProfile,
  };
}
